'use client'

import { UiIcon } from '@/components/ui/ui-icon'
import { useState } from 'react'
import Link from 'next/link'
import { MapPin, Sprout, Calendar, Camera } from 'lucide-react'
import { BreadcrumbComponent } from '@/components/ui/breadcrumb'
import { SEASON_LABEL, type CityCardView, type CitySeason } from '@/data/city-seo/card-view'
import { SEASON } from './components/seasons'
import { CityHubCard } from './components/city-hub-card'

type Filter = 'all' | CitySeason

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All cities' },
  { key: 'cool', label: 'Cool-season' },
  { key: 'warm', label: 'Warm-season' },
  { key: 'transition', label: 'Transition' },
]

export function LawnCareHubContent({ cities }: { cities: CityCardView[] }) {
  const [active, setActive] = useState<Filter>('all')

  const total = cities.length
  const zones = new Set(cities.map((c) => c.season)).size
  const counts: Record<CitySeason, number> = {
    cool: cities.filter((c) => c.season === 'cool').length,
    warm: cities.filter((c) => c.season === 'warm').length,
    transition: cities.filter((c) => c.season === 'transition').length,
  }
  const shown = active === 'all' ? cities : cities.filter((c) => c.season === active)

  return (
    <div className="overflow-x-clip">
      {/* ---------------- HERO ---------------- */}
      <header className="relative overflow-hidden" style={{ background: 'var(--grad-warm)' }}>
        <div
          aria-hidden
          className="pointer-events-none absolute -right-28 -top-48 h-[460px] w-[460px] rounded-full blur-[70px]"
          style={{ background: 'rgba(187,247,208,0.5)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-52 -left-28 h-[380px] w-[380px] rounded-full blur-[70px]"
          style={{ background: 'rgba(253,230,138,0.32)' }}
        />

        <div className="relative z-[1] content-rail">
          <div className="grid items-end gap-12 pb-[54px] pt-[46px] lg:grid-cols-[1.55fr_0.95fr]">
            <div>
              <BreadcrumbComponent items={[{ label: 'Home', href: '/' }, { label: 'Lawn Care' }]} />

              <p className="mb-3.5 inline-flex items-center gap-1.5 text-[0.82rem] font-bold uppercase tracking-[0.09em] text-green-700">
                <UiIcon icon={MapPin} size={16} />
                Local guides
              </p>

              <h1
                className="font-editorial m-0 mb-5 font-extrabold leading-[1.05] tracking-[-0.032em] text-[#14241b]"
                style={{ fontSize: 'clamp(2.3rem, 3.6vw + 0.7rem, 3.7rem)' }}
              >
                Local Lawn Care Guides
              </h1>

              <p className="m-0 max-w-[56ch] text-[1.2rem] leading-[1.62] text-[#41514a]">
                Lawn care is local. The right grass, the right feeding schedule, and the timing of things
                like crabgrass pre-emergent all come down to your climate and soil. Pick your city for a
                guide built around exactly that, plus the free tools to dial in the details.
              </p>

              <div className="mt-6 flex flex-wrap gap-x-7 gap-y-3.5 text-[0.93rem] text-[#41514a]">
                <span className="inline-flex items-center gap-2">
                  <UiIcon icon={MapPin} size={16} className="text-green-600" />
                  <strong className="font-bold text-[#14241b]">{total}</strong>&nbsp;cities live
                </span>
                <span className="inline-flex items-center gap-2">
                  <UiIcon icon={Sprout} size={16} className="text-green-600" />
                  <strong className="font-bold text-[#14241b]">{zones}</strong>&nbsp;climate zones
                </span>
                <span className="inline-flex items-center gap-2">
                  <UiIcon icon={Calendar} size={16} className="text-green-600" />
                  month-by-month schedules
                </span>
              </div>
            </div>

            {/* Browse-by-climate legend (doubles as the color key + a filter) */}
            <div className="ds-glass rounded-3xl p-5">
              <p className="mb-2 px-1.5 text-[0.72rem] font-bold uppercase tracking-[0.07em] text-[#6b7a73]">
                Browse by climate
              </p>
              {(['cool', 'warm', 'transition'] as CitySeason[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setActive(s)}
                  className="flex w-full items-center gap-3 rounded-xl min-h-11 px-3 py-2.5 text-start transition hover:bg-black/[0.04]"
                >
                  <span
                    className="h-[11px] w-[11px] shrink-0 rounded-full"
                    style={{ background: SEASON[s].accent, boxShadow: `0 0 0 4px ${SEASON[s].bg}` }}
                  />
                  <span className="text-[0.96rem] font-semibold text-[#14241b]">{SEASON_LABEL[s]}</span>
                  <span className="ms-auto text-[0.82rem] font-bold tabular-nums text-[#6b7a73]">
                    {counts[s]} {counts[s] === 1 ? 'city' : 'cities'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ---------------- CONTROLS + GRID + CTA ---------------- */}
      <div className="content-rail">
        <div className="my-6 flex flex-wrap items-center justify-between gap-4">
          <div
            role="group"
            aria-label="Filter cities by grass season"
            className="ds-glass inline-flex max-w-full flex-wrap gap-1 rounded-2xl p-1"
          >
            {FILTERS.map((f) => {
              const isActive = active === f.key
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActive(f.key)}
                  className="inline-flex min-h-11 min-w-0 max-w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-center text-[0.91rem] font-semibold transition"
                  style={
                    isActive
                      ? { background: 'rgba(255,255,255,0.88)', color: '#14241b', boxShadow: '0 4px 12px -4px rgba(15,40,25,0.18)' }
                      : { color: '#41514a' }
                  }
                >
                  {f.key !== 'all' && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: SEASON[f.key as CitySeason].accent }}
                    />
                  )}
                  {f.label}
                </button>
              )
            })}
          </div>

          <p className="text-[0.9rem] text-[#6b7a73]" aria-live="polite">
            {active === 'all' ? (
              <>
                Showing <strong className="font-bold text-[#14241b]">{total}</strong> of {total} cities
              </>
            ) : (
              <>
                Showing <strong className="font-bold text-[#14241b]">{shown.length}</strong>{' '}
                {SEASON_LABEL[active].toLowerCase()} {shown.length === 1 ? 'city' : 'cities'}
              </>
            )}
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {shown.map((c) => (
            <CityHubCard key={c.slug} city={c} headingLevel="h2" />
          ))}
        </div>

        {/* Closing CTA */}
        <section
          className="mb-20 mt-[72px] flex flex-wrap items-center justify-between gap-6 rounded-[28px] border border-white/55 p-5 sm:p-10"
          style={{
            background: 'linear-gradient(140deg, rgba(220,252,231,0.62), rgba(187,247,208,0.42))',
            backdropFilter: 'blur(16px) saturate(165%)',
            WebkitBackdropFilter: 'blur(16px) saturate(165%)',
            boxShadow: '0 20px 50px -20px rgba(22,163,74,0.26)',
          }}
        >
          <div>
            <h2 className="font-editorial m-0 mb-2 text-[1.6rem] font-extrabold tracking-[-0.025em] text-green-900">
              Don&apos;t see your city yet?
            </h2>
            <p className="m-0 max-w-[54ch] text-[1.02rem] leading-relaxed text-[#41514a]">
              We&apos;re adding new city guides every month. In the meantime, identify your grass from a
              photo and we&apos;ll build a plan around your exact climate and soil.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              href="/#analysis-card"
              prefetch={false}
              className="inline-flex min-h-[56px] items-center gap-2 rounded-full px-7 text-[1.05rem] font-semibold text-white transition hover:-translate-y-px"
              style={{ background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
            >
              <UiIcon icon={Camera} size={16} />
              Identify my grass
            </Link>
            <Link
              href="/guides"
              prefetch={false}
              className="inline-flex min-h-[56px] items-center rounded-full border border-gray-300 bg-white px-7 text-[1.05rem] font-semibold text-gray-800 transition hover:-translate-y-px hover:border-green-300 hover:text-green-700"
            >
              Browse all guides
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}
